import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Collapse, Link, Paper, Typography } from '@mui/material';
import {
  fetchActiveTrackers, getConsent, saveConsent, loadTrackers, relayPageView, areTrackersLoaded, areAdTrackersLoaded,
  isPrivatePath, isNoTrackerPath, purgeTrackerCookies, rememberUtmFromPrivatePage, TRACKER_PUBLIC_INFO,
} from '../utils/trackerLoader';

/* Bandeau de consentement + pilotage du chargeur de traceurs (src/utils/trackerLoader.js).
 * - N'apparaît QUE si au moins un traceur soumis à consentement est configuré dans la page
 *   admin « Trackers ». Choix du propriétaire (2026-09-19) : GA4, GTM et PostHog tournent dès l'arrivée
 *   en mode SANS COOKIE (voir trackerLoader.js) ; les pixels publicitaires attendent « Accepter ».
 * - « Refuser » est aussi simple qu'« Accepter » (exigence CNIL). Choix mémorisé 6 mois,
 *   modifiable à tout moment par le lien « Gérer mes cookies » (CookieSettingsLink, pied de page).
 * - Le suivi maison (tracking_visits / tracking_events, sans cookie tiers) n'est pas concerné. */

const OPEN_EVENT = 'jm:open-consent';

const useActiveTrackers = () => {
  const [trackers, setTrackers] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchActiveTrackers().then((list) => { if (alive) setTrackers(list); });
    return () => { alive = false; };
  }, []);
  return trackers;
};

export function CookieSettingsLink() {
  const trackers = useActiveTrackers();
  if (!trackers.length) return null;
  return (
    <Box sx={{ textAlign: 'center', pb: 1.5, mt: -1 }}>
      <Link component="button" type="button" underline="hover" color="text.secondary" sx={{ fontSize: 12 }}
        onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}>
        Gérer mes cookies
      </Link>
    </Box>
  );
}

export default function TrackerConsent() {
  const location = useLocation();
  const trackers = useActiveTrackers();
  const [consent, setConsent] = useState(getConsent());
  const [forcedOpen, setForcedOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const blockedHere = isNoTrackerPath(location.pathname);

  // Chargement en deux temps (mesure sans cookie dès l'arrivée, publicité après accord : voir
  // trackerLoader.js) et suivi des changements de page
  useEffect(() => {
    if (isPrivatePath(location.pathname)) {
      rememberUtmFromPrivatePage(location.search);
      // Filet de sécurité : si des traceurs tournent déjà dans cet onglet, on recharge la page
      // privée pour repartir sans eux (ils ne doivent jamais voir une adresse contenant un jeton).
      if (areTrackersLoaded()) window.location.reload();
      return;
    }
    if (blockedHere || !trackers.length) return;
    const wasLoaded = areTrackersLoaded();
    loadTrackers(trackers, consent === 'accepted');
    if (wasLoaded) relayPageView();
  }, [location.pathname, location.search, consent, trackers, blockedHere]);

  useEffect(() => {
    const open = () => { setForcedOpen(true); setShowDetails(true); };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  const choose = useCallback((choice) => {
    const hadLoaded = areAdTrackersLoaded();
    saveConsent(choice);
    setConsent(choice);
    setForcedOpen(false);
    if (choice === 'refused') {
      purgeTrackerCookies();
      if (hadLoaded) window.location.reload(); // seul moyen sûr d'arrêter des scripts déjà chargés
    }
  }, []);

  const visible = trackers.length > 0 && !blockedHere && (consent === null || forcedOpen);
  if (!visible) return null;

  return (
    <Paper elevation={8} role="dialog" aria-label="Choix concernant les cookies"
      sx={{ position: 'fixed', zIndex: 1400, left: { xs: 8, sm: 16 }, right: { xs: 8, sm: 'auto' }, bottom: { xs: 8, sm: 16 },
        maxWidth: 460, p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Cookies et mesure d’audience</Typography>
      <Typography variant="body2" color="text.secondary">
        Nous mesurons la fréquentation de la boutique sans cookie. Avec votre accord, nous utilisons aussi des outils
        publicitaires (efficacité de nos publicités). Vous pouvez refuser : le site fonctionne exactement pareil.{' '}
        <Link component="button" type="button" onClick={() => setShowDetails((v) => !v)} sx={{ verticalAlign: 'baseline' }}>
          {showDetails ? 'Masquer le détail' : 'En savoir plus'}
        </Link>
      </Typography>
      <Collapse in={showDetails}>
        <Box component="ul" sx={{ pl: 2.5, my: 1, fontSize: 13, color: 'text.secondary' }}>
          {trackers.map((t) => (
            <li key={t.key}><strong>{TRACKER_PUBLIC_INFO[t.key]?.name || t.key}</strong> : {TRACKER_PUBLIC_INFO[t.key]?.purpose}</li>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          Les outils publicitaires ne sont activés qu’après votre accord ; la mesure de fréquentation fonctionne sans cookie.
          Aucun de ces outils n’est jamais actif sur les cartes ni sur les faire-part animés que vous partagez avec vos proches.
          Votre choix est conservé 6 mois ; vous pouvez le modifier à tout moment avec le lien « Gérer mes cookies » en bas de page.
          {consent && <> Choix actuel : <strong>{consent === 'accepted' ? 'accepté' : 'refusé'}</strong>.</>}
        </Typography>
      </Collapse>
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={() => choose('refused')}>Refuser</Button>
        <Button fullWidth variant="outlined" color="inherit" onClick={() => choose('accepted')}>Accepter</Button>
      </Box>
    </Paper>
  );
}
